import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Edit3, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Profiles() {
  const { user, pickProfile } = useAuth();
  const nav = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", avatar: "", is_kids: false });
  const [edit, setEdit] = useState(false);

  const load = async () => {
    try {
      const [p, a] = await Promise.all([api.get("/profiles"), api.get("/profiles/avatars")]);
      setProfiles(p.data);
      setAvatars(a.data);
    } catch (e) { toast.error("Failed to load profiles"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/profiles", form);
      setProfiles((p) => [...p, data]);
      setShow(false); setForm({ name: "", avatar: "", is_kids: false });
      toast.success("Profile created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this profile?")) return;
    try { await api.delete(`/profiles/${id}`); setProfiles(p => p.filter(x => x.profile_id !== id)); }
    catch { toast.error("Failed"); }
  };

  const select = (p) => {
    pickProfile(p);
    nav("/browse");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-6 py-12">
      <h1 className="text-4xl md:text-6xl font-semibold mb-12">Who's watching?</h1>
      <div className="flex flex-wrap gap-6 justify-center max-w-5xl">
        {profiles.map((p) => (
          <div key={p.profile_id} data-testid={`profile-${p.profile_id}`} className="flex flex-col items-center group cursor-pointer">
            <button
              onClick={() => !edit && select(p)}
              className="relative w-32 h-32 md:w-40 md:h-40 rounded-md overflow-hidden ring-2 ring-transparent group-hover:ring-[#E50914] transition-all"
            >
              <img src={p.avatar} alt={p.name} className="w-full h-full object-cover bg-[#1a1a1a]" />
              {edit && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <Edit3 className="w-8 h-8 text-white" />
                </div>
              )}
            </button>
            <span className="mt-3 text-gray-400 group-hover:text-white transition-colors">{p.name}</span>
            {p.is_kids && <span className="text-xs text-[#E50914] mt-1">KIDS</span>}
            {edit && (
              <button onClick={() => del(p.profile_id)} className="mt-2 text-red-500 text-xs hover:text-red-400 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        ))}
        {profiles.length < 5 && (
          <button
            data-testid="add-profile-btn"
            onClick={() => setShow(true)}
            className="flex flex-col items-center group"
          >
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-md border-2 border-dashed border-gray-600 group-hover:border-white flex items-center justify-center transition-colors">
              <Plus className="w-16 h-16 text-gray-500 group-hover:text-white" />
            </div>
            <span className="mt-3 text-gray-400 group-hover:text-white">Add Profile</span>
          </button>
        )}
      </div>
      {profiles.length > 0 && (
        <button
          data-testid="manage-profiles-btn"
          onClick={() => setEdit(!edit)}
          className="mt-10 border border-gray-600 text-gray-400 hover:text-white hover:border-white px-6 py-2 uppercase tracking-widest text-sm transition-colors"
        >
          {edit ? "Done" : "Manage Profiles"}
        </button>
      )}

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="bg-[#141414] border border-white/10 rounded-lg p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-semibold mb-6">Add Profile</h2>
            <form onSubmit={create} className="space-y-4">
              <input data-testid="profile-name-input" required placeholder="Name" className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Choose avatar</label>
                <div className="grid grid-cols-6 gap-2">
                  {avatars.map((a) => (
                    <button key={a} type="button" onClick={() => setForm({ ...form, avatar: a })} className={`rounded-md overflow-hidden ring-2 transition-all ${form.avatar === a ? "ring-[#E50914]" : "ring-transparent"}`}>
                      <img src={a} className="w-full h-full bg-[#1a1a1a]" alt="" />
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_kids} onChange={(e) => setForm({ ...form, is_kids: e.target.checked })} />
                Kids profile
              </label>
              <div className="flex gap-3 pt-4">
                <button data-testid="profile-create-submit" type="submit" className="btn-red flex-1 py-3 rounded font-semibold">Create</button>
                <button type="button" onClick={() => setShow(false)} className="btn-glass flex-1 py-3 rounded font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
